//NOTE: you need to declare the parent component position: relative

import React, { FC, memo, useEffect, useState } from 'react';
import { Autocomplete } from '@material-ui/lab';
import { TextField, Checkbox, CircularProgress, Button } from '@material-ui/core';
import { slices, useAppDispatch } from 'src/redux';
import { isEmptyArray } from 'formik';

const { actions: manufacturerActions } = slices.manufacturer;

interface Props {
  label?: string;
  isMultiple?: boolean;
  onSelectionChange?: (selected: OptionType[] | OptionType | null) => void;
  categoryIds?: number[];
  categoryIsBlurred?: Boolean;
}

type OptionType = {
  id?: number;
  name?: string;
};

const Component: FC<Props> = ({
  label = 'Manufacturers',
  isMultiple = false,
  onSelectionChange,
  categoryIds,
  categoryIsBlurred
}) => {
  const dispatch = useAppDispatch();

  const [selectedOptions, setSelectedOptions] = useState<
    OptionType[] | OptionType | null
  >(isMultiple ? [] : null);
  const [manufacturerOptions, setManufacturerOptions] = useState<OptionType[]>(
    []
  );
  const [loading, setLoading] = useState(false);

  const handleSelectionChange = (
    event: React.ChangeEvent<{}>,
    newValue: OptionType[] | OptionType | null
  ) => {
    setSelectedOptions(newValue);
    if (onSelectionChange) {
      onSelectionChange(newValue); // Pass the selected options to the parent component
    }
  };

  useEffect(() => {
    const fetchManufacturersByCategory = async () => {
      try {
        if (!categoryIds || categoryIds.length === 0) return;

        setLoading(true);

        const promises = categoryIds.map((id: number) =>
          dispatch(
            manufacturerActions.getManufacturersByCategoryThunk({
              category_id: id
            })
          ).unwrap()
        );

        const results = await Promise.all(promises);

        const allOptions = results.flatMap((response) => {
          const manufacturers = response?.originalData.data || [];
          return manufacturers.map((item: any) => ({
            id: item?.id,
            name: item?.name
          }));
        });

        const uniqueOptions: OptionType[] = Array.from(
          new Map(allOptions.map((item) => [item.id, item])).values()
        );

        setManufacturerOptions(uniqueOptions || []);
      } catch (error) {
        console.error('Failed to fetch manufacturers by category:', error);
      } finally {
        setLoading(false);
      }
    };

    if (!categoryIsBlurred) return;
    fetchManufacturersByCategory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, categoryIsBlurred]);

  useEffect(() => {
    if (!categoryIds || categoryIds.length === 0) {
      setSelectedOptions([]);
      setManufacturerOptions([]);
      onSelectionChange?.([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryIds, isMultiple]);

  return (
    <div>
      <div>
        <Autocomplete
          fullWidth
          style={{
            // position: 'absolute',
            background: 'white'
          }}
          limitTags={1}
          multiple={isMultiple}
          options={manufacturerOptions}
          getOptionLabel={(option: OptionType) => option?.name || ''}
          value={selectedOptions}
          onChange={handleSelectionChange}
          renderOption={(option, { selected }) =>
            isMultiple ? (
              <>
                <Checkbox checked={selected} style={{ marginRight: 8 }} />
                {option.name}
              </>
            ) : (
              <>{option.name}</>
            )
          }
          renderInput={(params) => (
            <TextField
              {...params}
              variant="outlined"
              label={label}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loading ? (
                      <CircularProgress color="inherit" size={20} />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </>
                )
              }}
            />
          )}
          disabled={isEmptyArray(manufacturerOptions) || isEmptyArray(categoryIds)}
          loading={loading}
        />
      </div>
      <Button
        variant="outlined"
        color="primary"
        size='small'
        disabled={isEmptyArray(manufacturerOptions) || isEmptyArray(categoryIds) || loading}
        onClick={() => {
          setSelectedOptions(manufacturerOptions);
          onSelectionChange?.(manufacturerOptions);
        }}
      >
        Select All
      </Button>
    </div>
  );
};

export const ManufacturerDropdownList = memo(Component);
